import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "../lib/api";

/**
 * NotificationContext — single owner-side notification store.
 *
 * - Polls `/api/notifications/counts` every 5s (always, per user preference).
 * - When the `latest_at` changes, fetches new items via `since=<prev-latest>`
 *   and dispatches an onNew(items[]) event to subscribers (used by App.jsx
 *   to fire toast + sound + vibration + web notification).
 * - Exposes `counts` (total + by_section), `list`, and `refresh`, `open`,
 *   `markRead`, `markAllRead` helpers to consumers (Header bell, BottomNav
 *   badges, panel).
 */
const NotificationContext = createContext(null);

const EMPTY_COUNTS = {
  total: 0,
  by_section: { businesses: 0, enquiries: 0, orders: 0, employees: 0 },
  by_section_negative: { businesses: 0, enquiries: 0, orders: 0, employees: 0 },
  latest_at: null,
};

// Badge color classification, mirrored from notification_service.py's
// NEGATIVE_NOTIFICATION_TYPES — red (negative, needs attention) vs green
// (positive, default) for sub-tab/record-level dots computed client-side
// from the already-fetched `list` (no extra request).
const NEGATIVE_TYPES = new Set(["order_cancelled", "connection_rejected", "issue_raised"]);
export function polarityOf(type) {
  return NEGATIVE_TYPES.has(type) ? "negative" : "positive";
}

export function NotificationProvider({ enabled, onNew, children }) {
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [list, setList] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const latestRef = useRef(null);          // last-seen created_at ISO
  const bootstrapped = useRef(false);      // skip toast on the very first poll
  const markReadRef = useRef(null);        // latest markRead, read via ref so
                                            // refresh() doesn't need it as a
                                            // dependency (would churn the poll interval)

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data: c } = await apiClient.get("/notifications/counts");
      setCounts(c || EMPTY_COUNTS);
      const prev = latestRef.current;
      const next = c?.latest_at || null;
      if (next && next !== prev) {
        // Delta fetch — only rows created strictly AFTER our previous marker.
        const params = prev ? { params: { since: prev, limit: 20 } } : { params: { limit: 50 } };
        const { data: rows } = await apiClient.get("/notifications", params);
        if (Array.isArray(rows) && rows.length > 0) {
          setList((old) => {
            const seen = new Set(old.map((n) => n.id));
            const merged = [...rows.filter((r) => !seen.has(r.id)), ...old];
            return merged.slice(0, 200);
          });
          // Fire onNew only after the initial bootstrap so the user isn't
          // greeted with a toast avalanche for pre-existing rows on login.
          // Pass the current markRead so a toast/web-notification tap can
          // mark itself read immediately (#3 — correct read state).
          if (bootstrapped.current && typeof onNew === "function") {
            try { onNew(rows, markReadRef.current); } catch (_e) { /* callback errors must not break polling */ }
          }
        }
        latestRef.current = next;
      } else if (!prev && next) {
        latestRef.current = next;
      }
      bootstrapped.current = true;
    } catch (_e) {
      // 401 on logout etc — swallow, poll will resume when auth returns
    }
  }, [enabled, onNew]);

  // Initial fetch + 5s interval. Polls always, even when tab is hidden.
  useEffect(() => {
    if (!enabled) {
      setCounts(EMPTY_COUNTS);
      setList([]);
      latestRef.current = null;
      bootstrapped.current = false;
      return undefined;
    }
    let mounted = true;
    const load = async () => { if (mounted) await refresh(); };
    // First panel-list bootstrap — fill 50 recent so opening the drawer
    // immediately after login isn't empty.
    (async () => {
      try {
        const { data } = await apiClient.get("/notifications", { params: { limit: 50 } });
        if (mounted && Array.isArray(data)) setList(data);
      } catch (_e) { /* ignore */ }
      await load();
    })();
    const id = setInterval(load, 5000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [enabled, refresh]);

  const markRead = useCallback(async (id) => {
    try {
      await apiClient.post(`/notifications/${id}/read`);
      setList((old) => old.map((n) => (n.id === id ? { ...n, read: true } : n)));
      // Optimistic count refresh — server-side truth arrives on next poll.
      setCounts((c) => {
        const target = list.find((n) => n.id === id);
        if (!target || target.read) return c;
        const sec = target.section;
        const isNeg = polarityOf(target.type) === "negative";
        return {
          ...c,
          total: Math.max(0, c.total - 1),
          by_section: { ...c.by_section, [sec]: Math.max(0, (c.by_section[sec] || 0) - 1) },
          by_section_negative: isNeg
            ? { ...c.by_section_negative, [sec]: Math.max(0, (c.by_section_negative[sec] || 0) - 1) }
            : c.by_section_negative,
        };
      });
    } catch (_e) { /* silent — poll self-heals */ }
  }, [list]);

  useEffect(() => { markReadRef.current = markRead; }, [markRead]);

  const markAllRead = useCallback(async (section) => {
    try {
      await apiClient.post("/notifications/read-all", null, { params: section ? { section } : {} });
      setList((old) => old.map((n) => (section && n.section !== section ? n : { ...n, read: true })));
      setCounts((c) => {
        if (section) {
          const removed = c.by_section[section] || 0;
          return {
            ...c,
            total: Math.max(0, c.total - removed),
            by_section: { ...c.by_section, [section]: 0 },
            by_section_negative: { ...c.by_section_negative, [section]: 0 },
          };
        }
        return { ...c, total: 0, by_section: { businesses: 0, enquiries: 0, orders: 0, employees: 0 }, by_section_negative: { businesses: 0, enquiries: 0, orders: 0, employees: 0 } };
      });
    } catch (_e) { /* silent */ }
  }, []);

  const value = useMemo(
    () => ({ counts, list, panelOpen, setPanelOpen, refresh, markRead, markAllRead }),
    [counts, list, panelOpen, refresh, markRead, markAllRead],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    return {
      counts: EMPTY_COUNTS, list: [], panelOpen: false,
      setPanelOpen: () => {}, refresh: () => {}, markRead: () => {}, markAllRead: () => {},
    };
  }
  return ctx;
}

/** #3 — Correct read state: call from an entity-specific detail page (order,
 * enquiry) with that entity's id. Any unread notification pointing at this
 * exact entity gets marked read as soon as the page is viewed — no need to
 * separately open the notification panel. Safe to call with a falsy id. */
export function useMarkNotificationReadForEntity(entityId) {
  const { list, markRead } = useNotifications();
  useEffect(() => {
    if (!entityId) return;
    list.filter((n) => !n.read && n.entity_id === entityId).forEach((n) => { markRead(n.id); });
  }, [entityId, list, markRead]);
}

/** Deep-link route for a notification. Recipient perspective determines the
 * screen (e.g. `enquiry_new` is delivered to the supplier → open the incoming
 * enquiry response screen; `enquiry_responded` is delivered to the buyer →
 * open the outbound enquiry detail). */
export function routeForNotification(n) {
  if (!n) return "/";
  switch (n.type) {
    case "connection_request_new":
    case "connection_accepted":
    case "connection_rejected":
      return "/businesses";
    case "enquiry_new":
      return `/enquiries/incoming/${n.entity_id}`;
    case "enquiry_responded":
      return `/enquiries/${n.entity_id}`;
    case "order_new":
      return `/inbound-orders/${n.entity_id}`;
    case "order_in_progress":
    case "order_delivered":
    case "order_cancelled":
    case "order_placed":
      return `/orders/${n.entity_id}`;
    case "task_completed":
    case "task_in_progress":
    case "task_todo":
      return "/employees";
    case "task_assigned":
      return `/my-tasks/${n.entity_id}`;
    case "chat_message":
    case "issue_raised":
    case "issue_resolved":
      return `/chat/${n.entity_id}`;
    default:
      // Fallback by section
      switch (n.section) {
        case "businesses": return "/businesses";
        case "enquiries":  return "/enquiries";
        case "orders":     return "/orders";
        case "employees":  return "/employees";
        default: return "/";
      }
  }
}
